import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from '@/components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import { ThemeProvider } from './context/ThemeContext';

// Critical auth pages - loaded eagerly
import Login from './pages/Login';
import EticketBooking from './pages/eticket/EticketBooking';
import EticketSuccess from './pages/eticket/EticketSuccess';
import EticketView from './pages/eticket/EticketView';
import EticketScanner from './pages/eticket/EticketScanner';
import EticketAdmin from './pages/eticket/EticketAdmin';
import EticketReportPreview from './pages/eticket/SalesVerification';
import EticketSubscriptionSuccess from './pages/eticket/EticketSubscriptionSuccess';
import EticketProtectedRoute from './components/EticketProtectedRoute';
import LoginSelector from './pages/LoginSelector';
import UnifiedLogin from './pages/auth/UnifiedLogin';
import UnifiedSignup from './pages/auth/UnifiedSignup';
import VerifyEmail from './pages/auth/VerifyEmail';
import PostSignupOnboarding from './pages/auth/PostSignupOnboarding';
import ModuleSelector from './pages/auth/ModuleSelector';
import PendingActivation from './pages/auth/PendingActivation';

// Lazy-loaded PMS pages
const UnifiedAdminUsers = lazy(() => import('./pages/auth/UnifiedAdminUsers'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Properties = lazy(() => import('./pages/Properties'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Bookings = lazy(() => import('./pages/Bookings'));
const OwnerReports = lazy(() => import('./pages/OwnerReports'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Settings = lazy(() => import('./pages/Settings'));
const WebhookMonitor = lazy(() => import('./pages/WebhookMonitor'));
const POS = lazy(() => import('./pages/POS'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const OwnerAccess = lazy(() => import('./pages/OwnerAccess'));
const UserLogs = lazy(() => import('./pages/UserLogs'));
const Reports = lazy(() => import('./pages/Reports'));
const ScheduledReports = lazy(() => import('./pages/ScheduledReports'));
const RatesAvailability = lazy(() => import('./pages/RatesAvailability'));
const AccountReconciliation = lazy(() => import('./pages/AccountReconciliation'));
const Reviews = lazy(() => import('./pages/Reviews'));
const InvoiceReport = lazy(() => import('./pages/InvoiceReport'));
const WalkInRates = lazy(() => import('./pages/WalkInRates'));
const WalkInCounterRates = lazy(() => import('./pages/WalkInCounterRates'));
const Inbox = lazy(() => import('./pages/Inbox'));
const BookingEngine = lazy(() => import('./pages/BookingEngine'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const BookingEngineSettings = lazy(() => import('./pages/BookingEngineSettings'));
const Profile = lazy(() => import('./pages/Profile'));
const OmniChat = lazy(() => import('./pages/OmniChat'));
const EmailSettings = lazy(() => import('./pages/EmailSettings'));
const Beds24Docs = lazy(() => import('./pages/Beds24Docs'));
const MappingBuilder = lazy(() => import('./pages/MappingBuilder'));
const GmailSettings = lazy(() => import('./pages/GmailSettings'));
const RateSyncLogs = lazy(() => import('./pages/RateSyncLogs'));
const SecurityDeposits = lazy(() => import('./pages/SecurityDeposits'));
const OTACommission = lazy(() => import('./pages/OTACommission'));
const EldaPOSReport = lazy(() => import('./pages/EldaPOSReport'));
const DailySalesReport = lazy(() => import('./pages/DailySalesReport'));
const NetRevenueReport = lazy(() => import('./pages/NetRevenueReport'));
const MiscCharges = lazy(() => import('./pages/MiscCharges'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const FreeInvoiceGenerator = lazy(() => import('./pages/FreeInvoiceGenerator'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const EticketPlatformTerms = lazy(() => import('./pages/legal/EticketPlatformTerms'));
const TicketBuyerTerms = lazy(() => import('./pages/legal/TicketBuyerTerms'));
const SgmcTerms = lazy(() => import('./pages/legal/SgmcTerms'));
const IhccServicesAgreement = lazy(() => import('./pages/legal/IhccServicesAgreement'));
import DashboardLayout from './components/DashboardLayout';
import { EnvironmentBadge } from './components/EnvironmentBadge';
// e-Invoice Module
import { 
  EInvoiceDashboard,
  EInvoiceList,
  EInvoiceIntegrations,
  EInvoiceReports,
  InvoiceRequestForm,
  PortalAuth,
  PortalOnboarding,
  PortalDashboard,
  PortalPmsSettings,
  PortalInvoices,
  PortalReports,
  PortalBookings,
  PortalProperties,
  PortalCustomers,
  PortalIntegrations,
  PortalBilling,
  PortalSettings as PortalSettingsPage,
  PortalAutomation,
  PortalOwnerPayments,
  PortalAuditTrail,
  EInvoiceProductPage,
  GuestInvoicePayment,
  PortalQRInvoice,
  QRGuestForm,
  AdjustmentListPage,
  AdjustmentCreatePage,
  AdjustmentDetailPage
} from './pages/einvoice';
const PortalMappingBuilder = lazy(() => import('./pages/einvoice/PortalMappingBuilder'));
const PortalIntegrationDocs = lazy(() => import('./pages/einvoice/PortalIntegrationDocs'));
const PortalOtaCommission = lazy(() => import('./pages/einvoice/PortalOtaCommission'));
import { canAccessRoute, getFirstAccessibleRoute } from './utils/permissions';
const GuestCheckinForm = lazy(() => import('./pages/GuestCheckinForm'));
const GuestCheckoutForm = lazy(() => import('./pages/GuestCheckoutForm'));
const CheckInFormsPage = lazy(() => import('./pages/CheckInFormsPage'));
const CheckOutFormsPage = lazy(() => import('./pages/CheckOutFormsPage'));
const GuestNotificationSettings = lazy(() => import('./pages/GuestNotificationSettings'));
const HRJobApplicationForm = lazy(() => import('./pages/hr/HRJobApplicationForm'));
const HRApplicants = lazy(() => import('./pages/hr/HRApplicants'));
const HRApplicantDetail = lazy(() => import('./pages/hr/HRApplicantDetail'));
const HRSettings = lazy(() => import('./pages/hr/HRSettings'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminRoles = lazy(() => import('./pages/admin/AdminRoles'));
const AdminCompanies = lazy(() => import('./pages/admin/AdminCompanies'));
const AdminProperties = lazy(() => import('./pages/admin/AdminProperties'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminModulePricing = lazy(() => import('./pages/admin/AdminModulePricing'));
const AdminSecurity = lazy(() => import('./pages/admin/AdminSecurity'));
import axios from 'axios';

// Configure axios to automatically include Authorization header and user info
axios.interceptors.request.use(
  (config) => {
    // Try to get token from user object first
    let token = null;
    const userStr = localStorage.getItem('user');
    let userData = null;
    if (userStr) {
      try {
        userData = JSON.parse(userStr);
        token = userData.token;
      } catch (e) {
        // Invalid JSON
      }
    }
    // Fallback to separate token key
    if (!token) {
      token = localStorage.getItem('token');
    }
    
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Always include user identification headers for audit logging
    if (userData) {
      config.headers['X-User-Id'] = userData.id || 'unknown';
      config.headers['X-User-Name'] = userData.full_name || userData.name || 'Unknown';
      config.headers['X-User-Email'] = userData.email || '';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor: normalize error.response.data.detail to string
// Prevents "Objects are not valid as React child" crash when Pydantic validation errors
// (arrays of {type, loc, msg, input, url} objects) are passed to toast.error() or rendered
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail;
    if (detail && typeof detail !== 'string') {
      if (Array.isArray(detail)) {
        error.response.data.detail = detail
          .map(err => (typeof err === 'object' ? (err.msg || JSON.stringify(err)) : String(err)))
          .join(', ');
      } else if (typeof detail === 'object') {
        error.response.data.detail = JSON.stringify(detail);
      }
    }
    return Promise.reject(error);
  }
);

// Landing Pages
import Home from './pages/landing/Home';
import Features from './pages/landing/Features';
import Pricing from './pages/landing/Pricing';
import Contact from './pages/landing/Contact';
import Integrations from './pages/landing/Integrations';
import Beds24Integration from './pages/landing/Beds24Integration';
import Beds24EInvoicePlugin from './pages/landing/Beds24EInvoicePlugin';
import Beds24EInvoicePressRelease from './pages/landing/Beds24EInvoicePressRelease';
import Beds24EInvoiceFAQ from './pages/landing/Beds24EInvoiceFAQ';
import Beds24SetupGuide from './pages/landing/Beds24SetupGuide';
import Beds24PmsSetupGuide from './pages/landing/Beds24PmsSetupGuide';
import Beds24Hub from './pages/landing/beds24/Beds24Hub';
import Beds24Article from './pages/landing/beds24/Beds24Article';
import Developers from './pages/landing/Developers';
import ApiDocs from './pages/landing/ApiDocs';
import Partners from './pages/landing/Partners';
import SecurityCompliance from './pages/landing/SecurityCompliance';
import TrustCenter from './pages/landing/TrustCenter';
import CookiesPolicy from './pages/landing/CookiesPolicy';
import AboutUs from './pages/landing/AboutUs';
import IntellectualProperty from './pages/landing/IntellectualProperty';
import MystiCertified from './pages/landing/MystiCertified';
import GetStarted from './pages/landing/GetStarted';
import EInvoiceMalaysia from './pages/landing/EInvoiceMalaysia';
import LhdnEInvoice from './pages/landing/LhdnEInvoice';
import MyInvoisApi from './pages/landing/MyInvoisApi';
import HotelEInvoiceSystem from './pages/landing/HotelEInvoiceSystem';
import PmsEInvoiceIntegration from './pages/landing/PmsEInvoiceIntegration';
import ChannelManagerEInvoice from './pages/landing/ChannelManagerEInvoice';
import AirbnbEInvoiceMalaysia from './pages/landing/AirbnbEInvoiceMalaysia';
import MalaysiaEInvoiceSoftware from './pages/landing/MalaysiaEInvoiceSoftware';
import HotelPmsMalaysia from './pages/landing/HotelPmsMalaysia';
import HotelEInvoiceSystemMalaysia from './pages/landing/HotelEInvoiceSystemMalaysia';
import HomestayManagementSoftware from './pages/landing/HomestayManagementSoftware';
import PropertyManagementSoftware from './pages/landing/PropertyManagementSoftware';
import HotelPmsPenang from './pages/landing/HotelPmsPenang';
import WhatIsMalaysiaEInvoice from './pages/landing/WhatIsMalaysiaEInvoice';
import LhdnEInvoiceGuideHotels from './pages/landing/LhdnEInvoiceGuideHotels';
import BlogIndex from './pages/landing/blog/BlogIndex';
import BlogArticle from './pages/landing/blog/BlogArticle';
import StayMiiHR from './pages/landing/StayMiiHR';
import StayMiiCare from './pages/landing/StayMiiCare';
import CareGuide from './pages/landing/CareGuide';
import { CareLogin, CareForgot, CareReset } from './pages/care/CareAuth';
import CareDashboard from './pages/care/CareDashboard';
import CareResidents from './pages/care/CareResidents';
import CareResidentForm from './pages/care/CareResidentForm';
import CareResidentProfile from './pages/care/CareResidentProfile';
import { CareInvoices, CarePayments, CareReceipts } from './pages/care/CareFinance';
import { CareAdmissionForms, CareReports, CareUsers, CareSettings } from './pages/care/CareAdmin';
import CarePublicPay from './pages/care/CarePublicPay';
import CarePublicSign from './pages/care/CarePublicSign';
import CarePublicRegister from './pages/care/CarePublicRegister';
import CareOwnerSetup from './pages/care/CareOwnerSetup';
import EticketProduct from './pages/landing/EticketProduct';
import GoogleThingsToDo from './pages/landing/GoogleThingsToDo';
import GlobalMarketConnect from './pages/landing/GlobalMarketConnect';
import PressHub from './pages/landing/PressHub';
import IhccEticketingPressRelease from './pages/landing/IhccEticketingPressRelease';
import MystiCertificationPressRelease from './pages/landing/MystiCertificationPressRelease';
import FloatingShare from './components/FloatingShare';
import GlobalTixMouPressRelease from './pages/landing/GlobalTixMouPressRelease';
import SgmcLaunchPressRelease from './pages/landing/SgmcLaunchPressRelease';
import Beds24OfficialLaunchPressRelease from './pages/landing/Beds24OfficialLaunchPressRelease';

// ScrollToTop component to handle scroll on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const API = `${window.location.origin}/api`;

// Protected Route component that checks permissions
const ProtectedRoute = ({ user, onLogout, children, setUser }) => {
  const location = useLocation();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Check if user has permission to access this route
  if (!canAccessRoute(user, location.pathname)) {
    // Redirect to first accessible route
    const redirectTo = getFirstAccessibleRoute(user);
    return <Navigate to={redirectTo} replace />;
  }
  
  return (
    <DashboardLayout user={user} onLogout={onLogout} setUser={setUser}>
      {children}
    </DashboardLayout>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showForcedLogout, setShowForcedLogout] = useState(false);
  const [forcedLogoutMessage, setForcedLogoutMessage] = useState('');

  // Session heartbeat interval
  const heartbeatIntervalRef = React.useRef(null);
  const activityTimeoutRef = React.useRef(null);
  const warningTimeoutRef = React.useRef(null);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    
    if (storedUser && storedUser !== 'undefined') {
      try {
        const userData = JSON.parse(storedUser);
        
        // Check if token has expired
        if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
          console.log('Token expired, logging out...');
          clearSession();
        } else {
          setUser(userData);
        }
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        clearSession();
      }
    }
    setLoading(false);
  }, []);

  // Clear all session data
  const clearSession = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('session_id');
    localStorage.removeItem('session_timeout');
    localStorage.removeItem('session_warning');
  };

  // Reset activity timers
  const resetActivityTimers = useCallback(() => {
    const sessionTimeout = parseInt(localStorage.getItem('session_timeout') || '30');
    const sessionWarning = parseInt(localStorage.getItem('session_warning') || '25');
    
    // Clear existing timers
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    setShowSessionWarning(false);
    
    // Set warning timer (25 minutes default)
    warningTimeoutRef.current = setTimeout(() => {
      setShowSessionWarning(true);
    }, sessionWarning * 60 * 1000);
    
    // Set logout timer (30 minutes default)
    activityTimeoutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, sessionTimeout * 60 * 1000);
  }, []);

  // Handle session timeout
  const handleSessionTimeout = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: {
            'X-Session-Id': sessionId,
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (e) {
        console.error('Failed to logout on timeout:', e);
      }
    }
    
    setShowSessionWarning(false);
    setForcedLogoutMessage('Your session has expired due to inactivity. Please login again.');
    setShowForcedLogout(true);
    setUser(null);
    clearSession();
  };

  // Continue session (dismiss warning and reset timers)
  const handleContinueSession = async () => {
    setShowSessionWarning(false);
    resetActivityTimers();
    
    // Send heartbeat to server
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      try {
        await axios.post(`${API}/auth/session/heartbeat`, {}, {
          headers: { 'X-Session-Id': sessionId }
        });
      } catch (e) {
        console.error('Failed to send heartbeat:', e);
      }
    }
  };

  // Check session status periodically
  const checkSessionStatus = useCallback(async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId || !user) return;
    
    try {
      const response = await axios.post(`${API}/auth/session/check`, {}, {
        headers: { 'X-Session-Id': sessionId }
      });
      
      if (!response.data.valid && response.data.terminated) {
        // Session was terminated (force login from another device)
        setForcedLogoutMessage(response.data.message || 'Your session has been terminated because this account logged in from another device.');
        setShowForcedLogout(true);
        setUser(null);
        clearSession();
        
        // Clear intervals
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      }
    } catch (e) {
      console.error('Failed to check session:', e);
    }
  }, [user]);

  // Set up activity detection and heartbeat when user logs in
  useEffect(() => {
    if (user) {
      // Grace period: skip session check for 5 seconds after login to avoid
      // showing "Session Terminated" dialog from the previous session
      const graceTimeout = setTimeout(() => {
        checkSessionStatus();
        
        // Start heartbeat interval (every 60 seconds to keep session alive on server)
        heartbeatIntervalRef.current = setInterval(checkSessionStatus, 60 * 1000);
      }, 5000);
      
      // Check session when window gains focus (user switches back to tab)
      const handleFocus = () => {
        checkSessionStatus();
      };
      window.addEventListener('focus', handleFocus);
      
      // Listen for session-terminated event from API interceptor
      const handleSessionTerminated = (event) => {
        setForcedLogoutMessage(event.detail?.message || 'Your session has been terminated.');
        setShowForcedLogout(true);
        setUser(null);
        clearSession();
        
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      };
      window.addEventListener('session-terminated', handleSessionTerminated);
      
      // Set up activity listeners
      const handleActivity = () => resetActivityTimers();
      // Also check session on clicks (most intentional user actions)
      const handleClickWithSessionCheck = () => {
        resetActivityTimers();
        checkSessionStatus();
      };
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('click', handleClickWithSessionCheck);
      window.addEventListener('scroll', handleActivity);
      
      // Initialize timers
      resetActivityTimers();
      
      return () => {
        clearTimeout(graceTimeout);
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('session-terminated', handleSessionTerminated);
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('click', handleClickWithSessionCheck);
        window.removeEventListener('scroll', handleActivity);
      };
    }
  }, [user, resetActivityTimers, checkSessionStatus]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userData.token);
    
    // Store session info
    if (userData.session_id) {
      localStorage.setItem('session_id', userData.session_id);
    }
    if (userData.session_timeout_minutes) {
      localStorage.setItem('session_timeout', userData.session_timeout_minutes.toString());
    }
    if (userData.session_warning_minutes) {
      localStorage.setItem('session_warning', userData.session_warning_minutes.toString());
    }
    
    // Store token expiry time
    if (userData.token_expires_in) {
      const expiryTime = new Date(Date.now() + userData.token_expires_in * 1000);
      localStorage.setItem('tokenExpiry', expiryTime.toISOString());
    }
    
    setShowForcedLogout(false);
  };

  const handleLogout = async () => {
    const sessionId = localStorage.getItem('session_id');
    
    // Notify server of logout
    if (sessionId) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: {
            'X-Session-Id': sessionId,
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (e) {
        console.error('Failed to notify server of logout:', e);
      }
    }
    
    setUser(null);
    clearSession();
    
    // Clear intervals
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
  };

  // Session Warning Dialog
  const SessionWarningDialog = () => {
    if (!showSessionWarning) return null;
    
    const timeoutMinutes = parseInt(localStorage.getItem('session_timeout') || '30');
    const warningMinutes = parseInt(localStorage.getItem('session_warning') || '25');
    const remainingMinutes = timeoutMinutes - warningMinutes;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="session-warning-dialog">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="text-center mb-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900" data-testid="session-warning-title">Session Expiring</h2>
            <p className="text-slate-600 mt-2">
              Your session will expire in <span className="font-bold text-amber-600">{remainingMinutes} minutes</span> due to inactivity.
            </p>
          </div>
          <button
            onClick={handleContinueSession}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            data-testid="continue-session-btn"
          >
            Continue Session
          </button>
        </div>
      </div>
    );
  };

  // Forced Logout Dialog
  const ForcedLogoutDialog = () => {
    if (!showForcedLogout) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="forced-logout-dialog">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="text-center mb-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900" data-testid="forced-logout-title">Session Terminated</h2>
            <p className="text-slate-600 mt-2" data-testid="forced-logout-message">{forcedLogoutMessage}</p>
          </div>
          <button
            onClick={() => setShowForcedLogout(false)}
            className="w-full py-3 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-lg transition-colors"
            data-testid="forced-logout-ok-btn"
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-pulse text-slate-600 font-manrope">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <BrowserRouter>
        <ScrollToTop />
        <EnvironmentBadge />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
          {/* Public Landing Pages */}
          <Route path="/home" element={<Home />} />
          {/* StayMii E-Ticket module */}
          <Route path="/eticket/admin" element={<EticketProtectedRoute><EticketAdmin /></EticketProtectedRoute>} />
          <Route path="/eticket/admin/report" element={<EticketProtectedRoute><EticketReportPreview /></EticketProtectedRoute>} />
          <Route path="/eticket/scan" element={<EticketProtectedRoute><EticketScanner /></EticketProtectedRoute>} />
          <Route path="/eticket/ticket/:qrToken" element={<EticketView />} />
          <Route path="/eticket/subscription/success" element={<EticketSubscriptionSuccess />} />
          <Route path="/eticket/book/:slug/success" element={<EticketSuccess />} />
          <Route path="/eticket/book/:slug" element={<EticketBooking />} />
          {/* Legacy redirects */}
          <Route path="/eticket/:slug/success" element={<EticketSuccess />} />
          <Route path="/eticket/:slug" element={<EticketBooking />} />
          <Route path="/features" element={<Features />} />
          <Route path="/features/:category" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/integrations/beds24" element={<Beds24Integration />} />
          <Route path="/integrations/beds24-e-invoice" element={<Beds24EInvoicePlugin />} />
          <Route path="/integrations/beds24-e-invoice/faq" element={<Beds24EInvoiceFAQ />} />
          <Route path="/press/beds24-e-invoice-soft-launch" element={<Beds24EInvoicePressRelease />} />
          <Route path="/press/beds24-e-invoice-official-launch" element={<Beds24OfficialLaunchPressRelease />} />
          <Route path="/press/ihcc-eticketing-partnership" element={<IhccEticketingPressRelease />} />
          <Route path="/press/staymii-mysti-certified" element={<MystiCertificationPressRelease />} />
          <Route path="/press/staymii-globaltix-mou-nice-2026" element={<GlobalTixMouPressRelease />} />
          <Route path="/press/staymii-launches-sgmc-nice-2026" element={<SgmcLaunchPressRelease />} />
          <Route path="/press" element={<PressHub />} />
          <Route path="/integrations/beds24/setup-guide" element={<Beds24SetupGuide />} />
          <Route path="/integrations/beds24/pms-setup-guide" element={<Beds24PmsSetupGuide />} />
          <Route path="/beds24" element={<Beds24Hub />} />
          <Route path="/beds24/*" element={<Beds24Article />} />
          <Route path="/staymii-integrations" element={<Integrations />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/api" element={<Developers />} />
          <Route path="/docs" element={<ApiDocs />} />
          <Route path="/documentation" element={<ApiDocs />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/technology-partners" element={<Partners />} />
          <Route path="/security" element={<SecurityCompliance />} />
          <Route path="/trust" element={<TrustCenter />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/intellectual-property" element={<IntellectualProperty />} />
          <Route path="/certified-by-mysti" element={<MystiCertified />} />
          <Route path="/mysti" element={<MystiCertified />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/e-invoice-malaysia" element={<EInvoiceMalaysia />} />
          <Route path="/lhdn-e-invoice" element={<LhdnEInvoice />} />
          <Route path="/myinvois-api" element={<MyInvoisApi />} />
          <Route path="/hotel-e-invoice-system" element={<HotelEInvoiceSystem />} />
          <Route path="/pms-e-invoice-integration" element={<PmsEInvoiceIntegration />} />
          <Route path="/channel-manager-e-invoice" element={<ChannelManagerEInvoice />} />
          <Route path="/airbnb-e-invoice-malaysia" element={<AirbnbEInvoiceMalaysia />} />
          <Route path="/malaysia-einvoice-software" element={<MalaysiaEInvoiceSoftware />} />
          <Route path="/hotel-pms-malaysia" element={<HotelPmsMalaysia />} />
          <Route path="/hotel-einvoice-system-malaysia" element={<HotelEInvoiceSystemMalaysia />} />
          <Route path="/homestay-management-software-malaysia" element={<HomestayManagementSoftware />} />
          <Route path="/property-management-software-malaysia" element={<PropertyManagementSoftware />} />
          <Route path="/hotel-pms-penang" element={<HotelPmsPenang />} />
          <Route path="/what-is-malaysia-einvoice" element={<WhatIsMalaysiaEInvoice />} />
          <Route path="/lhdn-einvoice-guide-hotels" element={<LhdnEInvoiceGuideHotels />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/staymii-hr" element={<StayMiiHR />} />
          <Route path="/staymii-care" element={<StayMiiCare />} />
          <Route path="/staymii-care/guide" element={<CareGuide />} />
          <Route path="/e-ticket" element={<EticketProduct />} />
          <Route path="/eticket-product" element={<EticketProduct />} />
          <Route path="/google-things-to-do" element={<GoogleThingsToDo />} />
          <Route path="/global-market-connect" element={<GlobalMarketConnect />} />
          <Route path="/staymii-global-market-connect" element={<GlobalMarketConnect />} />
          <Route path="/sgmc" element={<GlobalMarketConnect />} />
          <Route path="/sell-tickets-on-google" element={<GoogleThingsToDo />} />
          <Route path="/integrations/google-things-to-do" element={<GoogleThingsToDo />} />
          <Route path="/free-hr-system-malaysia" element={<StayMiiHR />} />
          <Route path="/hr-recruitment-form" element={<StayMiiHR />} />
          <Route path="/hr-application-form" element={<StayMiiHR />} />
          <Route path="/free-job-application-form" element={<StayMiiHR />} />
          <Route path="/free-job-application-form-template" element={<StayMiiHR />} />
          <Route path="/job-application-form-template" element={<StayMiiHR />} />
          <Route path="/job-application-form-malaysia" element={<StayMiiHR />} />
          <Route path="/online-job-application-form" element={<StayMiiHR />} />
          <Route path="/employment-application-form-malaysia" element={<StayMiiHR />} />
          <Route path="/applicant-tracking-system-malaysia" element={<StayMiiHR />} />
          <Route path="/free-employee-application-form" element={<StayMiiHR />} />
          <Route path="/solutions" element={<Features />} />
          <Route path="/solutions/:type" element={<Features />} />
          
          {/* Unified Auth Routes */}
          <Route path="/auth/login" element={<UnifiedLogin />} />
          <Route path="/auth/signup" element={<UnifiedSignup />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/onboarding" element={<PostSignupOnboarding />} />
          <Route path="/auth/pending" element={<PendingActivation />} />
          <Route path="/auth/admin/users" element={<UnifiedAdminUsers />} />
          <Route path="/module-selector" element={<ModuleSelector />} />

          {/* Redirects from old login pages */}
          <Route
            path="/login"
            element={<Navigate to="/auth/login" replace />}
          />
          <Route
            path="/login/pms"
            element={<Navigate to="/auth/login" replace />}
          />
          <Route
            path="/"
            element={
              user ? (
                <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                  <Dashboard />
                </ProtectedRoute>
              ) : (
                <Home />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Properties />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Bookings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner-reports"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <OwnerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rates"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <RatesAvailability />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/webhooks"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <WebhookMonitor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <POS />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos/misc-charges"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <MiscCharges />
              </ProtectedRoute>
            }
          />
          <Route
            path="/elda-pos-report"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EldaPOSReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-sales-report"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <DailySalesReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/net-revenue-report"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <NetRevenueReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reconciliation"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <AccountReconciliation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner-access"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <OwnerAccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-logs"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <UserLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Reports user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduled-reports"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <ScheduledReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-sync-logs"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <RateSyncLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Reviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoice-report"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <InvoiceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/walkin-rates"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <WalkInRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/counter-rates"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <WalkInCounterRates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Inbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/omnichat"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <OmniChat user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-settings"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EmailSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gmail"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <GmailSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/integrations/beds24/docs"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Beds24Docs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/integrations/mapping-builder"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <MappingBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/security-deposits"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <SecurityDeposits />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ota-commission"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <OTACommission />
              </ProtectedRoute>
            }
          />
          {/* Public Booking Engine Routes (no auth required) */}
          <Route path="/booking" element={<BookingEngine />} />
          <Route path="/booking/confirmation" element={<BookingConfirmation />} />
          {/* Public Legal Pages (no auth required) */}
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/legal/eticket-platform-terms" element={<EticketPlatformTerms />} />
          <Route path="/legal/ticket-buyer-terms" element={<TicketBuyerTerms />} />
          <Route path="/legal/sgmc-terms" element={<SgmcTerms />} />
          <Route path="/legal/ihcc-services-agreement" element={<IhccServicesAgreement />} />
          <Route path="/contact" element={<Contact />} />
          {/* Admin Booking Engine Settings */}
          <Route
            path="/booking-engine"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <BookingEngineSettings />
              </ProtectedRoute>
            }
          />
          {/* User Profile - always accessible */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <Profile user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          {/* Contact Us page */}
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/free-invoice-generator" element={<FreeInvoiceGenerator />} />
          <Route path="/tools/einvoice" element={<FreeInvoiceGenerator />} />
          {/* e-Invoice Module Routes (inside StayMii Lite) */}
          <Route
            path="/einvoice"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EInvoiceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einvoice/dashboard"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EInvoiceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einvoice/invoices"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EInvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einvoice/integrations"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EInvoiceIntegrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einvoice/reports"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <EInvoiceReports />
              </ProtectedRoute>
            }
          />
          {/* CN/DN Adjustment pages - uses PortalLayout (eInvoice module), not DashboardLayout */}
          <Route path="/einvoice/portal/adjustment" element={<AdjustmentListPage />} />
          <Route path="/einvoice/portal/adjustment/create" element={<AdjustmentCreatePage />} />
          <Route path="/einvoice/portal/adjustment/:id" element={<AdjustmentDetailPage />} />
          {/* Public Invoice Request Form (no auth required) */}
          <Route path="/invoice-request/:token" element={<InvoiceRequestForm />} />
          
          {/* Public Guest Payment Page (no auth required) */}
          <Route path="/pay/:paymentToken" element={<GuestInvoicePayment />} />
          
          {/* E-Invoice Portal (Self-Service) - No main app auth required */}
          <Route path="/einvoice/portal/auth" element={<Navigate to="/auth/login" replace />} />
          <Route path="/einvoice/portal/onboarding" element={<PortalOnboarding />} />
          <Route path="/einvoice/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/einvoice/portal/invoices" element={<PortalInvoices />} />
          <Route path="/einvoice/portal/bookings" element={<PortalBookings />} />
          <Route path="/einvoice/portal/properties" element={<PortalProperties />} />
          <Route path="/einvoice/portal/customers" element={<PortalCustomers />} />
          <Route path="/einvoice/portal/integrations" element={<PortalIntegrations />} />
          <Route path="/einvoice/portal/ota-commission" element={<PortalOtaCommission />} />
          <Route path="/einvoice/portal/pms-settings" element={<PortalPmsSettings />} />
          <Route path="/einvoice/portal/reports" element={<PortalReports />} />
          <Route path="/einvoice/portal/billing" element={<PortalBilling />} />
          <Route path="/einvoice/portal/billing/success" element={<PortalBilling />} />
          <Route path="/einvoice/portal/mapping-builder" element={<PortalMappingBuilder />} />
          <Route path="/einvoice/portal/integration-docs" element={<PortalIntegrationDocs />} />
          {/* Redirect old subscription routes to billing */}
          <Route path="/einvoice/portal/subscription" element={<Navigate to="/einvoice/portal/billing" replace />} />
          <Route path="/einvoice/portal/subscription/success" element={<Navigate to="/einvoice/portal/billing" replace />} />
          {/* AUTOMATION temporarily disabled in e-Invoice module */}
          <Route path="/einvoice/portal/automation" element={<Navigate to="/einvoice/portal/dashboard" replace />} />
          <Route path="/einvoice/portal/settings" element={<PortalSettingsPage />} />
          <Route path="/einvoice/portal/owner-payments" element={<PortalOwnerPayments />} />
          <Route path="/einvoice/portal/audit-trail" element={<PortalAuditTrail />} />
          <Route path="/einvoice/portal/qr-invoice" element={<PortalQRInvoice />} />
          <Route path="/einvoice/portal/admin/user-approvals" element={<Navigate to="/einvoice/portal/settings" replace />} />
          <Route path="/einvoice/product" element={<EInvoiceProductPage />} />
          {/* Public QR e-Invoice Guest Form (no auth required) */}
          <Route path="/einvoice/form/:token" element={<QRGuestForm />} />

          {/* Public Guest Check-In / Check-Out Forms (no auth required) */}
          <Route path="/guest/checkin" element={<GuestCheckinForm />} />
          <Route path="/guest/checkin/:token" element={<GuestCheckinForm />} />
          <Route path="/guest/checkout" element={<GuestCheckoutForm />} />
          <Route path="/guest/checkout/:token" element={<GuestCheckoutForm />} />

          {/* Admin Guest Forms - Separate Pages */}
          <Route
            path="/guest-forms/checkins"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <CheckInFormsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guest-forms/checkouts"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <CheckOutFormsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guest-forms/notifications"
            element={
              <ProtectedRoute user={user} onLogout={handleLogout} setUser={setUser}>
                <GuestNotificationSettings />
              </ProtectedRoute>
            }
          />

          {/* HR Module Routes */}
          <Route path="/hr/:companySlug/apply" element={<HRJobApplicationForm />} />
          <Route path="/hr/apply" element={<HRJobApplicationForm demoMode />} />
          <Route path="/hr/portal/applicants" element={<HRApplicants />} />
          <Route path="/hr/portal/applicants/:id" element={<HRApplicantDetail />} />
          <Route path="/hr/portal/settings" element={<HRSettings />} />
          <Route path="/care" element={<Navigate to="/care/dashboard" replace />} />
          <Route path="/care/login" element={<CareLogin />} />
          <Route path="/care/forgot-password" element={<CareForgot />} />
          <Route path="/care/reset-password" element={<CareReset />} />
          <Route path="/care/dashboard" element={<CareDashboard />} />
          <Route path="/care/residents" element={<CareResidents />} />
          <Route path="/care/residents/new" element={<CareResidentForm />} />
          <Route path="/care/residents/:id/edit" element={<CareResidentForm />} />
          <Route path="/care/residents/:id" element={<CareResidentProfile />} />
          <Route path="/care/forms" element={<CareAdmissionForms />} />
          <Route path="/care/invoices" element={<CareInvoices />} />
          <Route path="/care/payments" element={<CarePayments />} />
          <Route path="/care/receipts" element={<CareReceipts />} />
          <Route path="/care/reports" element={<CareReports />} />
          <Route path="/care/users" element={<CareUsers />} />
          <Route path="/care/settings" element={<CareSettings />} />
          <Route path="/care/pay/:token" element={<CarePublicPay />} />
          <Route path="/care/sign/:token" element={<CarePublicSign />} />
          <Route path="/care/register" element={<CarePublicRegister />} />
          <Route path="/care/register/:slug" element={<CarePublicRegister />} />
          <Route path="/care/owner-setup" element={<CareOwnerSetup />} />

          {/* Admin Control Center Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="properties" element={<AdminProperties />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="module-pricing" element={<AdminModulePricing />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
          </Route>
        </Routes>
        </Suspense>
        {/* Session Management Dialogs */}
        <SessionWarningDialog />
        <ForcedLogoutDialog />
        <CookieConsent />
        <FloatingShare />
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </ThemeProvider>
  );
}

export default App;